import { Button, Dropdown, message } from 'antd';
import { MenuProps } from 'antd';
import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MenuOutlined } from '@ant-design/icons';
import { useToggle } from 'react-use';

import { ConfirmationModal } from '../../../shared/components/confirmation-modal';
import { Feature } from '../../../shared/models/features';
import { Role } from '../../../shared/models/role';
import { translationNamespace } from '../../constants/translation-resources';
import { useDeleteRoleMutation } from '../../data/mutations/role-mutations';
import { permissionSelector } from '../../../shared/data/store/selectors/auth-selectors';
import { AppResources } from '../../../shared/constants/app-resources';

interface ActionsProps {
  role: Role;
}

export const Actions: FC<ActionsProps> = ({ role }) => {
  const { t } = useTranslation(translationNamespace);
  const deleteMutation = useDeleteRoleMutation();
  const permissions = useSelector(permissionSelector);
  const [showConfirmDeleteModal, toggleShowConfirmDeleteModal] = useToggle(false);

  const remove = useCallback(() => {
    if (role.id == null) {
      return;
    }

    deleteMutation.mutate(role.id, {
      onSuccess: () => {
        message.success(t('roles.shared.deleteSuccessMessage'));
        toggleShowConfirmDeleteModal();
      },
    });
  }, [deleteMutation, t, role.id, toggleShowConfirmDeleteModal]);

  const items = useMemo<MenuProps['items']>(() => {
    const menuItems: MenuProps['items'] = [];

    if (permissions[AppResources.ROLE]['UPDATE']) {
      menuItems.push({
        key: 'edit',
        label: <Link to={`roles/${role.id}`}>{t(`${Feature.SHARED}:edit`)}</Link>,
      });
    }
    if (permissions[AppResources.ROLE]['DELETE']) {
      menuItems.push({
        key: 'remove',
        onClick: () => toggleShowConfirmDeleteModal(),
        label: t(`${Feature.SHARED}:remove`),
      });
    }

    return menuItems;
  }, [permissions, role.id, t, toggleShowConfirmDeleteModal]);

  return (
    <>
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button type="link" icon={<MenuOutlined />}>
          {t(`${Feature.SHARED}:actions`)}
        </Button>
      </Dropdown>
      <ConfirmationModal
        open={showConfirmDeleteModal}
        title={t(`${Feature.SHARED}:areYouSure`)}
        onCancel={toggleShowConfirmDeleteModal}
        onOk={remove}
        content={t(`${Feature.SHARED}:confirmDeleteMsg`, {
          resourceName: role.name,
          buttonTitle: t(`${Feature.SHARED}:confirm`),
        })}
      />
    </>
  );
};
